import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { newsCategoryApi } from "api/newsCategory.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ModalStatus } from "types/modal";
import { NewsCategory } from "types/news";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { $url } from "utils/url";
import { NewsCategoryModal } from "./components/NewsCategoryModal";

interface NewsQuery extends QueryParam {
  areaId: number | null;
}

export const NewsCategoryPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<NewsQuery>({
    page: 1,
    limit: 10,
    search: "",
    areaId: null,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NewsCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState<Partial<NewsCategory>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await newsCategoryApi.findAll(query);
    setLoading(false);
    setData(res.data.newsCategories);
    setTotal(res.data.total);
  };

  const handleDelete = async (news: NewsCategory) => {
    setLoading(true);
    try {
      await newsCategoryApi.delete(news.id);
      fetchData();
      message.info("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo<TableColumnsType<NewsCategory>>(
    () => [
      {
        title: "STT",
        key: "stt",
        width: 80,
        render: (_, __, index) => (
          <span>{(query.page - 1) * query.limit + index + 1}</span>
        ),
      },
      {
        title: "Danh mục",
        dataIndex: "title",
        key: "title",
        render: (_, record) => (
          <INA
            image={record.iconUrl ? $url(record.iconUrl) : undefined}
            name={record.name}
          additional={`ID: ${record.id}`}
          />
        ),
      },
      {
        title: "Thao tác",
        key: "action",
        width: 120,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedNews(record);
                  setVisibleModal(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                placement="top"
                title="Xác nhận xóa danh mục này?"
                onConfirm={() => handleDelete(record)}
                okText="Xóa"
                cancelText="Không"
              >
                <ActionButton aria-label="Xóa">
                  <DeleteOutlined style={{ color: "#ef4444" }} />
                </ActionButton>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [query.limit, query.page],
  );

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              placeholder: "Tìm kiếm",
              onChange: (value) => {
                setSearch(value);
                debounceSearch(value);
              },
              inputProps: {
                allowClear: {
                  clearIcon: (
                    <CloseCircleFilled
                      onClick={() => {
                        setSearch("");
                        setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                      }}
                    />
                  ),
                },
              },
            }}
            leftActions={
              <AppButton
                tone="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVisibleModal(true);
                  setModalStatus("create");
                  setSelectedNews({});
                }}
              >
                Thêm mới
              </AppButton>
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<NewsCategory>
            loading={loading}
            pagination={false}
            rowKey="id"
            dataSource={data}
            columns={columns}
            scroll={{ x: 760 }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={({ limit, page }) => {
              query.page = page;
              query.limit = limit;
              setQuery({ ...query });
            }}
          />
        }
      />

      <NewsCategoryModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        news={selectedNews}
        status={modalStatus}
      />
    </div>
  );
});
